import { apiFetch, authHeaders, refreshAndGetHeaders, sessionExpiredBus, type McpAuthorizationWorkspace } from '../api';
import { McpAuthRequestError, safeErrorCode } from './mcp-authorize-errors';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

/** GET /api/v1/mcp-auth/requests/:id/workspaces for the MCP authorize page. Unlike the shared
 * `get` in api.ts (which turns every failure into a bare message), a failure keeps the HTTP
 * status and the API's {error} code as McpAuthRequestError, so the page can tell "not signed
 * in" (401, or no session at all) from a bridge failure (503) or a refusal (400). Uses the same
 * header lookup and the same bounded 401 refresh-and-replay (apiFetch) as every portal call. */
export async function getMcpAuthorizationWorkspaces(id: string): Promise<{ client_name: string; workspaces: McpAuthorizationWorkspace[] }> {
  let headers = await authHeaders();
  if (!headers.Authorization) headers = await refreshAndGetHeaders();
  if (!headers.Authorization) throw new McpAuthRequestError(401, 'unauthenticated');
  let res: Response;
  try {
    res = await apiFetch(`${API_BASE}/api/v1/mcp-auth/requests/${encodeURIComponent(id)}/workspaces`, { headers });
  } catch {
    // apiFetch throws only once the session is known dead, or when the network fails.
    throw sessionExpiredBus.expired ? new McpAuthRequestError(401, 'unauthenticated') : new McpAuthRequestError(0, 'network_error');
  }
  if (!res.ok) {
    if (res.status === 401) throw new McpAuthRequestError(401, 'unauthenticated');
    const body: unknown = await res.json().catch(() => null);
    const raw = body && typeof body === 'object' ? (body as { error?: unknown }).error : undefined;
    throw new McpAuthRequestError(res.status, safeErrorCode(raw, res.status));
  }
  return res.json() as Promise<{ client_name: string; workspaces: McpAuthorizationWorkspace[] }>;
}
